/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { FONT, RADIUS, SURFACE, TEXT } from '../../theme/tokens'

const Label = ({ children }) => (
    <Typography
        variant="overline"
        sx={{ display: 'block', color: TEXT.muted, mb: 0.5 }}
    >
        {children}
    </Typography>
)
Label.propTypes = { children: PropTypes.node }

const Field = ({ label, children }) => (
    <Box>
        <Label>{label}</Label>
        {children}
    </Box>
)
Field.propTypes = { label: PropTypes.string, children: PropTypes.node }

const ColorField = ({ label, value, onChange }) => (
    <Field label={label}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
                component="input"
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label={label}
                sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    border: `1px solid ${SURFACE.borderHover}`,
                    borderRadius: `${RADIUS.sm}px`,
                    background: 'transparent',
                    cursor: 'pointer',
                }}
            />
            <TextField
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{ flex: 1 }}
                inputProps={{ 'aria-label': `${label} hex` }}
            />
        </Box>
    </Field>
)
ColorField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
}

const AlignField = ({ value, onChange }) => (
    <Field label="Align">
        <TextField
            select
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {['left', 'center', 'right'].map((a) => (
                <MenuItem key={a} value={a}>
                    {a}
                </MenuItem>
            ))}
        </TextField>
    </Field>
)
AlignField.propTypes = { value: PropTypes.string, onChange: PropTypes.func }

/**
 * A bounded integer input that lets the user type freely and clamps on blur.
 *
 * Clamping on every keystroke made multi-digit entry impossible: in a field
 * with min 10, typing "2" became 10 at once and the next "4" made it 104
 * (then 100). Now an in-range value is applied as soon as it is typed (the
 * canvas updates live), an out-of-range or empty draft is left alone while
 * typing, and blur clamps it into [min, max] (empty reverts to the current
 * value).
 */
const NumberField = ({ label, value, onChange, min = 0, max = 100 }) => {
    const [draft, setDraft] = useState(String(value ?? ''))
    // A different block (or an undo) changes `value` from outside.
    useEffect(() => setDraft(String(value ?? '')), [value])

    const parse = (text) => {
        const n = parseInt(text, 10)
        return Number.isNaN(n) ? null : n
    }

    return (
        <Field label={label}>
            <TextField
                type="number"
                fullWidth
                value={draft}
                onChange={(e) => {
                    setDraft(e.target.value)
                    const n = parse(e.target.value)
                    if (n !== null && n >= min && n <= max && n !== value)
                        onChange(n)
                }}
                onBlur={() => {
                    const n = parse(draft)
                    const next =
                        n === null ? value : Math.min(max, Math.max(min, n))
                    setDraft(String(next ?? ''))
                    if (next !== value) onChange(next)
                }}
                inputProps={{ min, max, 'aria-label': label }}
            />
        </Field>
    )
}
NumberField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.number,
    onChange: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
}

/** Property editor for the selected block (or body settings when none). */
const Inspector = ({ block, body, onChangeProps, onChangeBody }) => {
    if (!block) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="caption" sx={{ color: TEXT.muted }}>
                    Select a block to edit it, or adjust the email body below.
                </Typography>
                <ColorField
                    label="Page background"
                    value={body.backgroundColor}
                    onChange={(v) => onChangeBody({ backgroundColor: v })}
                />
                <ColorField
                    label="Content background"
                    value={body.contentBackground}
                    onChange={(v) => onChangeBody({ contentBackground: v })}
                />
                <NumberField
                    label="Content width (px)"
                    value={body.contentWidth}
                    onChange={(v) => onChangeBody({ contentWidth: v })}
                    min={320}
                    max={800}
                />
            </Box>
        )
    }

    const p = block.props
    const set = (key) => (v) => onChangeProps({ [key]: v })
    const setEvt = (key) => (e) => onChangeProps({ [key]: e.target.value })

    const editors = {
        heading: (
            <>
                <Field label="Text">
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={p.text}
                        onChange={setEvt('text')}
                    />
                </Field>
                <Field label="Size">
                    <TextField
                        select
                        fullWidth
                        value={p.level}
                        onChange={setEvt('level')}
                    >
                        <MenuItem value="h1">Large (H1)</MenuItem>
                        <MenuItem value="h2">Medium (H2)</MenuItem>
                        <MenuItem value="h3">Small (H3)</MenuItem>
                    </TextField>
                </Field>
                <ColorField
                    label="Color"
                    value={p.color}
                    onChange={set('color')}
                />
                <AlignField value={p.align} onChange={set('align')} />
                <NumberField
                    label="Vertical padding"
                    value={p.paddingY}
                    onChange={set('paddingY')}
                />
            </>
        ),
        paragraph: (
            <>
                <Field label="Text">
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={p.text}
                        onChange={setEvt('text')}
                    />
                </Field>
                <ColorField
                    label="Color"
                    value={p.color}
                    onChange={set('color')}
                />
                <AlignField value={p.align} onChange={set('align')} />
                <NumberField
                    label="Font size"
                    value={p.fontSize}
                    onChange={set('fontSize')}
                    min={10}
                    max={32}
                />
                <NumberField
                    label="Vertical padding"
                    value={p.paddingY}
                    onChange={set('paddingY')}
                />
            </>
        ),
        image: (
            <>
                <Field label="Image URL">
                    <TextField
                        fullWidth
                        value={p.src}
                        onChange={setEvt('src')}
                        placeholder="https://…"
                    />
                </Field>
                <Field label="Alt text">
                    <TextField
                        fullWidth
                        value={p.alt}
                        onChange={setEvt('alt')}
                    />
                </Field>
                <Field label="Link URL (optional)">
                    <TextField
                        fullWidth
                        value={p.href}
                        onChange={setEvt('href')}
                        placeholder="https://…"
                    />
                </Field>
                <NumberField
                    label="Width (px)"
                    value={p.width}
                    onChange={set('width')}
                    min={40}
                    max={800}
                />
                <AlignField value={p.align} onChange={set('align')} />
            </>
        ),
        button: (
            <>
                <Field label="Label">
                    <TextField
                        fullWidth
                        value={p.text}
                        onChange={setEvt('text')}
                    />
                </Field>
                <Field label="Link URL">
                    <TextField
                        fullWidth
                        value={p.href}
                        onChange={setEvt('href')}
                        placeholder="https://…"
                    />
                </Field>
                <ColorField
                    label="Background"
                    value={p.background}
                    onChange={set('background')}
                />
                <ColorField
                    label="Text color"
                    value={p.color}
                    onChange={set('color')}
                />
                <AlignField value={p.align} onChange={set('align')} />
                <NumberField
                    label="Corner radius"
                    value={p.borderRadius}
                    onChange={set('borderRadius')}
                    max={30}
                />
            </>
        ),
        divider: (
            <>
                <ColorField
                    label="Color"
                    value={p.color}
                    onChange={set('color')}
                />
                <NumberField
                    label="Vertical padding"
                    value={p.paddingY}
                    onChange={set('paddingY')}
                />
            </>
        ),
        spacer: (
            <NumberField
                label="Height (px)"
                value={p.height}
                onChange={set('height')}
                min={4}
                max={200}
            />
        ),
        columns: (
            <>
                <Field label="Left column">
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={p.leftText}
                        onChange={setEvt('leftText')}
                    />
                </Field>
                <Field label="Right column">
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={p.rightText}
                        onChange={setEvt('rightText')}
                    />
                </Field>
                <ColorField
                    label="Text color"
                    value={p.color}
                    onChange={set('color')}
                />
                <NumberField
                    label="Font size"
                    value={p.fontSize}
                    onChange={set('fontSize')}
                    min={10}
                    max={32}
                />
            </>
        ),
        rawHtml: (
            <Field label="HTML">
                <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={p.html}
                    onChange={setEvt('html')}
                    sx={{
                        '& textarea': {
                            typography: 'caption',
                            fontFamily: FONT.mono,
                        },
                    }}
                />
            </Field>
        ),
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editors[block.type] ?? null}
        </Box>
    )
}

Inspector.propTypes = {
    block: PropTypes.object,
    body: PropTypes.object.isRequired,
    onChangeProps: PropTypes.func.isRequired,
    onChangeBody: PropTypes.func.isRequired,
}

export default Inspector
